"use client";

import React, { useState } from "react";
import { ArrowRight, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle, MapPin, MessageCircle, PhoneCall, Search, Sparkles } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const faqItems = [{
  id: "faq-1",
  question: "How does Local SEO help my business in Hull and East Yorkshire?",
  answer: "Local SEO ensures your business appears prominently when potential customers in Hull, Beverley, Cottingham, and surrounding areas search for your services on Google. By optimizing your Google Business Profile, acquiring localized citations, and building proximity signals, I help you secure top rankings in the Google Map Pack and organic local search results—driving direct phone calls, foot traffic, and high-converting leads.",
  category: "General Local SEO"
}, {
  id: "faq-2",
  question: "Will I work directly with you, Yassine, or an account manager?",
  answer: "You work 100% directly with me. Unlike traditional digital agencies where your account gets passed down to junior executives or outsourced staff, every local strategy, technical audit, and Google Business Profile optimization is personally planned and executed by me, Yassine Essaadi.",
  category: "Working With Me"
}, {
  id: "faq-3",
  question: "How long does it take to see local SEO results in the Hull market?",
  answer: "Most local Hull businesses start seeing measurable increases in Map Pack visibility and local query rankings within 3 to 6 months. Less competitive local niches or highly optimized Google Business Profiles can see quick wins within 30 to 60 days once primary location signals and category data are corrected.",
  category: "Timelines & Expectations"
}, {
  id: "faq-4",
  question: "What is the difference between Local SEO and National SEO?",
  answer: "National SEO targets broad keywords across the entire country without location constraints. Local SEO focuses specifically on geographic search intent—capturing users searching for local services in Hull, Hessle, or East Riding through Map Pack listings, localized content, local schema markup, and customer reviews.",
  category: "General Local SEO"
}, {
  id: "faq-5",
  question: "Do I need a physical office in Hull to rank in the local Map Pack?",
  answer: "A physical address or defined service area within Hull or East Riding is necessary for Google Business Profile verification. If you operate a service-area business (such as a trade professional or traveling consultant), I optimize your profile to cover specific HU postcodes (HU1 to HU20) effectively without revealing a private home address.",
  category: "Google Business Profile"
}, {
  id: "faq-6",
  question: "How do you optimize my Google Business Profile for the Hull Map Pack?",
  answer: "I conduct a deep audit of your primary and secondary categories, service lists, geo-tagged photo assets, business description, and localized FAQs. I also set up a clear review acquisition strategy so satisfied local Hull clients naturally boost your local entity authority.",
  category: "Google Business Profile"
}, {
  id: "faq-7",
  question: "Can you help multi-location businesses target multiple areas around the Humber?",
  answer: "Yes. I construct custom multi-location landing page architectures and separate Google Business strategies for businesses spanning Hull, Scunthorpe, Grimsby, Beverley, and York, ensuring each location ranks for its respective area without triggering duplicate content issues.",
  category: "Strategy & Expansion"
}, {
  id: "faq-8",
  question: "How do you approach local link building in East Riding?",
  answer: "I focus strictly on authentic local link building—securing listings in verified Hull directories, regional media coverage, local business sponsorships, and contextual links from trusted East Yorkshire partners rather than spammy, automated link networks.",
  category: "Strategy & Expansion"
}, {
  id: "faq-9",
  question: "What makes your consultancy different from a traditional digital agency in Hull?",
  answer: "Complete accountability and direct consultant access. Agencies often charge high monthly retainers to cover expensive office space and sales teams, then assign your account to junior staff. As an independent consultant, you receive tailored strategy, direct communication, and execution from an experienced specialist.",
  category: "Working With Me"
}, {
  id: "faq-10",
  question: "How do you measure and report on local SEO success?",
  answer: "I provide transparent reporting on metrics that directly impact your revenue: Google Map Pack phone calls, direction requests, organic traffic from Hull searchers, contact form submissions, and localized rank tracking across target postcodes.",
  category: "Timelines & Expectations"
}, {
  id: "faq-11",
  question: "Is AI Search Optimisation (GEO) included in your Local SEO service?",
  answer: "Yes. Modern local search is evolving with Google AI Overviews and conversational AI models. I structure your local business entity data, structured markup, and online review footprint so AI engines cite and recommend your business when searchers ask for recommendations in Hull.",
  category: "AI & Modern Search"
}, {
  id: "faq-12",
  question: "How do we get started with a local SEO strategy for my business?",
  answer: "The best starting point is to book a free consultation directly with me. I will review your current local search footprint in Hull, analyze your main local competitors, and discuss a practical roadmap tailored to your growth goals.",
  category: "Working With Me"
}];
export default function Localseofaqs() {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredFaqs = faqItems.filter(item => item.question.toLowerCase().includes(searchQuery.toLowerCase()) || item.answer.toLowerCase().includes(searchQuery.toLowerCase()) || item.category.toLowerCase().includes(searchQuery.toLowerCase()));
  return <section id="localseofaqs" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="absolute -top-24 -left-24 size-96 rounded-full bg-secondary/30 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -right-24 size-96 rounded-full bg-muted/50 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-5xl">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground rounded-full border border-border">
            <MapPin className="size-3.5 text-primary" />
            Hull & East Yorkshire Local SEO
          </Badge>

          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-4">
            Frequently Asked Questions About{" "}
            <span className="text-primary">Local SEO in Hull</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear, honest answers about ranking in the Google Map Pack, working directly with an independent SEO consultant, and driving real commercial growth in East Riding.
          </p>

          <div className="mt-8 relative max-w-lg mx-auto">
            <div className="relative flex items-center">
              <Search className="absolute left-4 size-5 text-muted-foreground pointer-events-none" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search local SEO questions (e.g. Map Pack, pricing, working with me)..." className="w-full bg-card text-card-foreground placeholder:text-muted-foreground border border-border rounded-xl pl-12 pr-4 py-3.5 text-sm shadow-xs focus:outline-hidden focus:ring-2 focus:ring-ring/50 focus:border-border transition-all" />
            </div>
            {searchQuery && <p className="text-xs text-muted-foreground text-left mt-2 pl-2">
                Showing {filteredFaqs.length} of {faqItems.length} questions
              </p>}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-sm p-4 sm:p-8 mb-12">
          {filteredFaqs.length === 0 ? <div className="text-center py-12 px-4">
              <HelpCircle className="size-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <h3 className="text-lg font-semibold text-foreground mb-1">
                No matching questions found
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {"Try searching for another keyword or feel free to contact me directly with your question."}
              </p>
              <Button variant="outline" onClick={() => setSearchQuery("")} className="rounded-lg border-border">
                Clear Search
              </Button>
            </div> : <Accordion type="single" collapsible className="w-full space-y-4">
              {filteredFaqs.map((item, i) => <AccordionItem data-index={i} key={item.id} value={item.id} className="border border-border/80 rounded-xl px-4 sm:px-6 bg-background/50 hover:bg-background transition-colors overflow-hidden">
                  <AccordionTrigger className="hover:no-underline py-5 text-left group">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 pr-2">
                      <span className="font-serif text-base sm:text-lg font-bold text-primary group-hover:text-primary/80 transition-colors">
                        {item.question}
                      </span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pt-1 pb-6 text-foreground/90 text-sm sm:text-base leading-relaxed border-t border-border/40 mt-1">
                    <p className="mb-4">{item.answer}</p>

                    <div className="flex items-center gap-2 pt-2 text-xs font-medium text-muted-foreground">
                      <Badge variant="outline" className="rounded-md bg-muted/50 text-muted-foreground border-border/60 font-normal">
                        {item.category}
                      </Badge>
                      <span className="inline-block size-1 rounded-full bg-border" />
                      <span className="flex items-center gap-1 text-primary">
                        <CheckCircle2 className="size-3.5" />
                        Direct Consultant Execution
                      </span>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>}
        </div>

        <div className="bg-gradient-to-br from-card via-muted/40 to-secondary/30 border border-border rounded-2xl p-6 sm:p-10 shadow-md text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="size-4" />
              Have A Specific Question About Your Business?
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
              {"Ready to grow your local search presence in Hull?"}
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {"You work directly with me—no account managers or middleman agency fees. Book a free consultation to discuss your local SEO strategy today."}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl font-medium shadow-sm transition-all">
              <Link to="/ContactPage">
                <MessageCircle className="size-4 mr-2" />
                Book Free Consultation
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border bg-background hover:bg-accent text-foreground rounded-xl">
              <Link to="/LocalSEOPage">
                Explore Local SEO
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}